import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

const QUERY_PARAM = 'q';
const INDEX_VIEW = 'search-index';
const INDEX_PAGE_SIZE = 250;
const SEARCHING_ATTRIBUTE = 'searching';

/**
 * @typedef {{ id: string, terms: string, position: number }} IndexEntry
 */

/**
 * Searches every product in the current collection view (respecting active filters and sort)
 * without leaving the page.
 *
 * A lightweight index (collection.search-index template, 250 products per request) is used for
 * matching, and full product cards are fetched only for grid pages that contain matches.
 */
class CollectionSearch extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLButtonElement | null} */
  #clearButton = null;
  /** @type {HTMLElement | null} */
  #emptyState = null;
  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {Promise<IndexEntry[]> | null} */
  #indexPromise = null;
  /** @type {Map<number, Promise<Map<string, Element>>>} */
  #cardPages = new Map();
  /** @type {Element[] | null} */
  #originalChildren = null;
  /** @type {Map<Element, string>} */
  #originalCountText = new Map();

  #query = '';
  #runId = 0;
  /** @type {MutationObserver | null} */
  #rerenderObserver = null;

  connectedCallback() {
    this.#input = this.querySelector('[data-collection-search-input]');
    this.#clearButton = this.querySelector('[data-collection-search-clear]');
    this.#emptyState = this.querySelector('[data-collection-search-empty]');
    this.#status = this.querySelector('[data-collection-search-status]');

    this.querySelector('[data-collection-search-form]')?.addEventListener('submit', this.#handleSubmit);
    this.#input?.addEventListener('input', this.#handleInput);
    this.#input?.addEventListener('focus', this.#preload);
    this.#input?.addEventListener('keydown', this.#handleKeydown);
    this.#clearButton?.addEventListener('click', this.#handleClear);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    const initialQuery = new URL(window.location.href).searchParams.get(QUERY_PARAM)?.trim();
    if (initialQuery && this.#input) {
      this.#input.value = initialQuery;
      this.#search(initialQuery);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#rerenderObserver?.disconnect();
  }

  get #resultsList() {
    return this.closest('results-list');
  }

  get #grid() {
    return /** @type {HTMLElement | null} */ (this.#resultsList?.querySelector('ul[ref="grid"]') ?? null);
  }

  get #pageSize() {
    return Number(this.dataset.pageSize) || 24;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    event.preventDefault();
    this.#search(this.#input?.value ?? '');
    this.#input?.blur();
  };

  #handleInput = debounce(() => {
    this.#search(this.#input?.value ?? '');
  }, 200);

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#input?.value) {
      event.preventDefault();
      this.#handleClear();
    }
  };

  #handleClear = () => {
    if (!this.#input) return;
    this.#input.value = '';
    this.#search('');
    this.#input.focus();
  };

  #preload = () => {
    this.#loadIndex().catch(() => {});
  };

  /**
   * Builds a URL for the current collection view (filters + sort) without search-only params.
   * @param {Record<string, string>} params
   */
  #viewURL(params) {
    const url = new URL(window.location.href);
    url.searchParams.delete(QUERY_PARAM);
    url.searchParams.delete('section_id');
    url.searchParams.delete('view');
    url.hash = '';
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
    return url;
  }

  /** @param {string} rawQuery */
  async #search(rawQuery) {
    const query = rawQuery.trim().toLowerCase();
    const runId = ++this.#runId;

    this.#query = query;
    this.#updateURL(rawQuery.trim());
    if (this.#clearButton) this.#clearButton.hidden = query === '';

    if (!query) {
      this.#restore();
      return;
    }

    this.#resultsList?.setAttribute(SEARCHING_ATTRIBUTE, '');
    this.setAttribute('aria-busy', 'true');

    const tokens = query.split(/\s+/);
    /** @type {Element[]} */
    let matches;

    try {
      const index = await this.#loadIndex();
      if (runId !== this.#runId) return;

      const matchedEntries = index.filter(({ terms }) => tokens.every((token) => terms.includes(token)));
      const pageNumbers = new Set(matchedEntries.map(({ position }) => Math.floor(position / this.#pageSize) + 1));
      const pages = await Promise.all([...pageNumbers].map((page) => this.#loadCardPage(page)));
      if (runId !== this.#runId) return;

      const cardsById = new Map(pages.flatMap((page) => [...page]));
      matches = matchedEntries.map(({ id }) => cardsById.get(id)).filter((card) => card !== undefined);
    } catch {
      if (runId !== this.#runId) return;
      matches = this.#currentChildren().filter((card) => {
        const text = (card.textContent ?? '').toLowerCase();
        return tokens.every((token) => text.includes(token));
      });
    }

    this.removeAttribute('aria-busy');
    this.#render(matches);
  }

  /** @returns {Promise<IndexEntry[]>} */
  #loadIndex() {
    if (this.#indexPromise) return this.#indexPromise;

    /** @param {number} page */
    const fetchIndexPage = async (page) => {
      const response = await fetch(this.#viewURL({ view: INDEX_VIEW, page: String(page) }));
      if (!response.ok) throw new Error(`Search index request failed: ${response.status}`);
      return /** @type {{ pages: number, items: [number, string][] }} */ (await response.json());
    };

    const promise = (async () => {
      const first = await fetchIndexPage(1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(first.pages - 1, 0) }, (_, index) => fetchIndexPage(index + 2))
      );

      return [first, ...rest].flatMap(({ items }, pageIndex) =>
        items.map(([id, terms], itemIndex) => ({
          id: String(id),
          terms,
          position: pageIndex * INDEX_PAGE_SIZE + itemIndex,
        }))
      );
    })();

    this.#indexPromise = promise;
    promise.catch(() => {
      if (this.#indexPromise === promise) this.#indexPromise = null;
    });

    return promise;
  }

  /**
   * @param {number} page
   * @returns {Promise<Map<string, Element>>}
   */
  #loadCardPage(page) {
    const cached = this.#cardPages.get(page);
    if (cached) return cached;

    const sectionId = this.getAttribute('section-id') ?? '';
    const promise = sectionRenderer.getSectionHTML(sectionId, true, this.#viewURL({ page: String(page) })).then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      /** @type {Map<string, Element>} */
      const cards = new Map();
      for (const card of doc.querySelectorAll('ul[ref="grid"] > [ref="cards[]"]')) {
        if (card instanceof HTMLElement && card.dataset.productId) cards.set(card.dataset.productId, card);
      }
      return cards;
    });

    this.#cardPages.set(page, promise);
    promise.catch(() => {
      if (this.#cardPages.get(page) === promise) this.#cardPages.delete(page);
    });

    return promise;
  }

  /** @returns {Element[]} */
  #currentChildren() {
    return this.#originalChildren ?? Array.from(this.#grid?.children ?? []);
  }

  /** @param {Element[]} matches */
  #render(matches) {
    const grid = this.#grid;
    if (!grid) return;

    if (!this.#originalChildren) {
      this.#originalChildren = Array.from(grid.children);
      for (const counter of this.#counters()) {
        this.#originalCountText.set(counter, counter.textContent ?? '');
      }
    }

    grid.replaceChildren(...matches);
    if (this.#emptyState) this.#emptyState.hidden = matches.length > 0;

    const countText = this.#formatCount(matches.length);
    for (const counter of this.#counters()) counter.textContent = countText;
    if (this.#status) this.#status.textContent = countText;
  }

  #restore() {
    const grid = this.#grid;

    if (grid && this.#originalChildren) {
      grid.replaceChildren(...this.#originalChildren);
      for (const [counter, text] of this.#originalCountText) counter.textContent = text;
    }

    this.#originalChildren = null;
    this.#originalCountText.clear();
    this.removeAttribute('aria-busy');
    this.#resultsList?.removeAttribute(SEARCHING_ATTRIBUTE);
    if (this.#emptyState) this.#emptyState.hidden = true;
    if (this.#status) this.#status.textContent = '';
  }

  /** @returns {Element[]} */
  #counters() {
    return Array.from(this.#resultsList?.querySelectorAll('.products-count-wrapper > span') ?? []);
  }

  /** @param {number} count */
  #formatCount(count) {
    const template = (count === 1 ? this.dataset.countOne : this.dataset.countOther) ?? '[count]';
    return template.replace('[count]', String(count));
  }

  /** @param {string} query */
  #updateURL(query) {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set(QUERY_PARAM, query);
    } else {
      url.searchParams.delete(QUERY_PARAM);
    }
    if (url.href !== window.location.href) history.replaceState(history.state, '', url);
  }

  /**
   * Filters and sorting re-render the section from the server, so the index and cached cards are
   * stale and the active query has to be applied again once the new grid is in place.
   */
  #handleFilterUpdate = () => {
    this.#runId++;
    this.#indexPromise = null;
    this.#cardPages.clear();
    this.#originalChildren = null;
    this.#originalCountText.clear();

    if (!this.#query) return;

    const resultsContainer = this.#resultsList?.querySelector('#ResultsList');
    if (!resultsContainer) return;

    this.#rerenderObserver?.disconnect();
    const observer = new MutationObserver((records) => {
      const gridChanged = records.some(
        (record) =>
          record.type === 'attributes' ||
          (record.target instanceof Element && !record.target.closest('[ref="cards[]"]'))
      );
      if (!gridChanged) return;

      observer.disconnect();
      if (this.#rerenderObserver === observer) this.#rerenderObserver = null;
      if (this.isConnected && this.#query) this.#search(this.#input?.value ?? this.#query);
    });
    this.#rerenderObserver = observer;
    observer.observe(resultsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-last-page', 'data-product-id'],
    });
    setTimeout(() => observer.disconnect(), 5000);
  };
}

if (!customElements.get('collection-search')) {
  customElements.define('collection-search', CollectionSearch);
}
